import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useTasks } from "@/hooks/useTasks";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { Mic, RotateCcw } from "lucide-react";
import { format } from "date-fns";

interface FollowUpReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  customerName?: string;
  connectorId?: string;
  smContactId?: string;
  onSave: (content: string, dueDate: Date) => void;
}

export function FollowUpReminderDialog({
  open,
  onOpenChange,
  leadId,
  customerName,
  connectorId,
  smContactId,
  onSave,
}: FollowUpReminderDialogProps) {
  const { addTask } = useTasks();
  const { user } = useAuth();
  const { registerModal, unregisterModal } = useModal();

const [description, setDescription] = useState("");
const initialDue = new Date(Date.now() + 10 * 60 * 1000);
const [dueDate, setDueDate] = useState(format(initialDue, "yyyy-MM-dd"));
const [dueTime, setDueTime] = useState(format(initialDue, "HH:mm"));
const [isSubmitting, setIsSubmitting] = useState(false);

  const { isListening, toggleVoiceInput } = useSpeechRecognition({
    onTranscript: (text) =>
      setDescription((prev) => {
        const newText = prev ? `${prev} ${text}` : text;
        return newText.substring(0, 500);
      }),
  });

  useEffect(() => {
    if (!open) return;
    registerModal("followup-reminder-dialog", () => onOpenChange(false), 1);
    return () => unregisterModal("followup-reminder-dialog");
  }, [open, onOpenChange, registerModal, unregisterModal]);

const resetForm = () => {
  const initialDue = new Date(Date.now() + 10 * 60 * 1000);
  setDescription("");
  setDueDate(format(initialDue, "yyyy-MM-dd"));
  setDueTime(format(initialDue, "HH:mm"));
};

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please enter what to remind");
      return;
    }

    const dueDateTime = new Date(`${dueDate}T${dueTime}`);
    if (Number.isNaN(dueDateTime.getTime())) {
      toast.error("Please select a valid due date and time");
      return;
    }
    if (dueDateTime.getTime() <= Date.now()) {
      toast.error("Reminder due date/time must be in the future");
      return;
    }

    const normalizedDescription = description.trim();
    setIsSubmitting(true);

    try {
      await addTask({
        title: normalizedDescription.split("\n")[0].substring(0, 50),
        description: normalizedDescription,
        type: "reminder",
        leadId,
        connectorId,
        smContactId,
        dueDate: dueDateTime,
        priority: "medium",
        completed: false,
        attachments: [],
        createdBy: user?.id || "unknown",
        createdByName: user?.name || "Unknown",
        createdAt: new Date(),
      });

      onSave(normalizedDescription, dueDateTime);
      resetForm();
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving follow-up reminder:", err);
      toast.error("Failed to save reminder");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center gap-2">
            <div className="text-center">
              <DialogTitle className="text-xl font-bold">Add Reminder</DialogTitle>
              {customerName && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  For: {customerName}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={resetForm}
              className="h-8 w-8 rounded-lg shrink-0"
              title="Clear form"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Description */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <div className="relative">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter reminder details or tap mic to speak..."
                rows={4}
                maxLength={500}
                className="resize-none pr-12"
              />
              <Button
                type="button"
                variant={isListening ? "default" : "ghost"}
                size="icon"
                className={`absolute right-2 top-2 h-8 w-8 rounded-full ${
                  isListening
                    ? "bg-red-500 hover:bg-red-600 animate-pulse"
                    : ""
                }`}
                onClick={toggleVoiceInput}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                <Mic className={`h-4 w-4 ${isListening ? "text-white" : ""}`} />
              </Button>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">
                {isListening
                  ? "🔴 Listening... tap mic to stop"
                  : "Tap mic for voice input • Max 500 characters"}
              </span>
              <span
                className={
                  description.length > 450
                    ? "text-warning font-medium"
                    : "text-muted-foreground"
                }
              >
                {description.length}/500
              </span>
            </div>
          </div>

          {/* Due Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="block [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Time *</Label>
              <Input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="block [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985]"
        >
          {isSubmitting ? "Saving..." : "Create Reminder"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
